/**
 * Normalizes heading case from all-caps to Title Case
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text
 */
export const normalizeHeadingCase = (text) => {
  if (!text || typeof text !== "string") return text;

  // Strip "Section:" / "SECTION:" if it exists at the start
  let processedText = text.replace(/^(?:SECTION|Section):\s*/i, "").trim();

  const isAllCaps =
    processedText.length >= 3 &&
    processedText === processedText.toUpperCase() &&
    /[A-Z]/.test(processedText) &&
    processedText !== processedText.toLowerCase(); // Has uppercase letters

  if (isAllCaps) {
    return processedText
      .split(/\s+/)
      .map((word) => {
        if (word.length === 0) return word;
        const match = word.match(/^([^a-zA-Z]*)([a-zA-Z]+)([^a-zA-Z]*)$/);
        if (match) {
          const prefix = match[1] || "";
          const letters = match[2];
          const suffix = match[3] || "";

          // Special case: Keep "AI" in uppercase
          if (letters.toUpperCase() === "AI") {
            return prefix + "AI" + suffix;
          }

          // Special case: Keep Roman numerals in uppercase (e.g., "VII.")
          if (/^[ivxlcdm]+$/i.test(letters) && /^(?:m{0,4}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:\bix|\biv|v?i{0,3}))$/i.test(letters)) {
            return prefix + letters.toUpperCase() + suffix;
          }

          return (
            prefix +
            letters.charAt(0).toUpperCase() +
            letters.slice(1).toLowerCase() +
            suffix
          );
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }

  return processedText;
};

/**
 * Removes citation markers like [1], [2], etc. from text
 * @param {string} text - The text to process
 * @returns {string} - Text with citation markers removed
 */
export const removeCitationMarkers = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(/\[\d+\]/g, "");
};

/**
 * Converts plain text to formatted HTML with proper headings, lists, etc.
 * @param {string} value - The text to convert
 * @returns {string} - Formatted HTML
 */
export const convertTextToHtml = (value = "") => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") {
    try {
      value = String(value);
    } catch (e) {
      console.error("Error converting value to string:", e);
      return "";
    }
  }
  // First, detect and convert all-caps headings in plain text BEFORE HTML detection
  // This ensures headings are preserved even if text gets wrapped in HTML later
  let processedValue = value;

  // IMPORTANT: avoid flipping into "HTML mode" mid-stream just because a model outputs
  // inline tags like <br> or <strong>. That can cause formatting to suddenly disappear.
  // We treat text as HTML only if it contains block/structural HTML tags.
  const containsBlockHtml = (text) => {
    if (!text || typeof text !== "string") return false;
    return /<\/?(?:p|ul|ol|li|h[1-6]|blockquote|pre|code|div)(?:\s[^>]*?)?>/i.test(
      text,
    );
  };

  const stripInlineHtmlToText = (text) => {
    if (!text || typeof text !== "string") return text;
    // Convert <br> to newline, strip other inline tags while preserving inner text.
    let out = text.replace(/<br\s*\/?>/gi, "\n");
    out = out.replace(
      /<\/?(?:strong|em|b|i|u|span|a)(?:\s[^>]*?)?>/gi,
      "",
    );
    return out;
  };

  const hasBlockHtml = containsBlockHtml(processedValue);
  if (!hasBlockHtml) {
    processedValue = stripInlineHtmlToText(processedValue);
  }

  const hasHtmlTags = hasBlockHtml;

  // Only preprocess if text is NOT already HTML (to avoid double-processing)
  // Detect all-caps headings in plain text (before HTML processing)
  // This handles cases where text might be plain during streaming but HTML on completion
  if (
    !hasHtmlTags &&
    typeof processedValue === "string" &&
    processedValue.length > 0
  ) {
    // Split by lines and check each line for all-caps headings
    const lines = processedValue.split("\n");
    const processedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();

      // SAFETY NET: Detect standard section names even without markers
      const standardSections = [
        "Issue(s) Presented",
        "Relevant Rules / Legal Principles",
        "Analysis",
        "Application",
        "Conclusion",
        "Litigation Risk Assessment",
        "Strategic Recommendations",
      ];

      // Check for exact match or Roman numeral prefix (e.g., "I. Issue(s) Presented")
      const isStandardSection = standardSections.some((section) => {
        const escaped = section.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`^(?:[IVXLCDM]+\\.\\s+)?${escaped}\\s*:?\\s*$`, "i");
        return regex.test(trimmedLine);
      });

      if (isStandardSection) {
        // Find which section it matches to get the proper casing
        const sectionMatch = standardSections.find((section) => {
          const escaped = section.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(
            `^(?:[IVX]+\\.\\s+)?${escaped}\\s*:?\\s*$`,
            "i",
          );
          return regex.test(trimmedLine);
        });

        // Preserve Roman numeral if it existed, otherwise use raw match
        const romanMatch = trimmedLine.match(/^([IVXLCDM]+\.)\s/i);
        const prefix = romanMatch ? `${romanMatch[1].toUpperCase()} ` : "";
        // Numbered (Roman numeral) section headings remain true headings (larger)
        if (romanMatch) {
          return `<h1><strong>${prefix}${sectionMatch}</strong></h1>`;
        }
        // Other standard sections are regular section headings
        return `<h2><strong>${sectionMatch}</strong></h2>`;
      }

      // More lenient detection - allow some punctuation but still detect all-caps headings
      const isAllCaps =
        trimmedLine.length >= 3 &&
        trimmedLine.length < 120 &&
        trimmedLine === trimmedLine.toUpperCase() &&
        /[A-Z]/.test(trimmedLine) &&
        !trimmedLine.match(/^[-•*▪▫◦‣⁃\d]/) &&
        !trimmedLine.match(/^\d+[.)]/) &&
        !trimmedLine.match(/^#{1,6}\s/) &&
        trimmedLine.split(/\s+/).length <= 10 &&
        !trimmedLine.match(/^[A-Z\s]+[a-z]/) && // Not mixed case
        // Allow headings that end with colon or are followed by blank line (common in legal docs)
        (trimmedLine.endsWith(":") || trimmedLine.match(/^[A-Z\s()/-]+$/));

      if (isAllCaps) {
        const nextLine =
          index < lines.length - 1 ? lines[index + 1].trim() : "";
        const prevLine = index > 0 ? lines[index - 1].trim() : "";
        // If it looks like a heading (preceded by blank or followed by content)
        if (prevLine === "" || (nextLine !== "" && !/^[-•\d]/.test(nextLine))) {
          // Remove trailing colon if present for normalization
          const textToNormalize = trimmedLine.endsWith(":")
            ? trimmedLine.slice(0, -1)
            : trimmedLine;
          const normalizedText = normalizeHeadingCase(textToNormalize);
          return `<h2><strong>${normalizedText}</strong></h2>`;
        }
      }
      return line;
    });
    processedValue = processedLines.join("\n");
  }

  const isHtml = hasBlockHtml;
  if (isHtml) {
    let html = processedValue;

    // CRITICAL FIX: Fix malformed numbered items where markdown bold was removed incorrectly
    // Pattern like "**1. Priority**" becomes "1. Priority" but sometimes becomes "1Priority"
    html = html.replace(/^(\s*)(\d+)([A-Z])/gm, "$1$2. $3");
    html = html.replace(/^(\s*)(\d+)\.([A-Z])/gm, "$1$2. $3");

    // CRITICAL FIX: Convert markdown headings to HTML even when HTML is detected
    // Handle "### SECTION: Heading Name" format specifically
    html = html.replace(
      /^(#{1,6})\s+(?:SECTION:\s*)?(.+)$/gm,
      (match, hashes, text) => {
        const level = Math.min(hashes.length, 6);
        const tag = `h${level}`;
        // Strip "SECTION:" if it's still in the text
        let headingText = text.trim().replace(/^SECTION:\s*/i, "");
        const normalizedText = normalizeHeadingCase(headingText);
        return `<${tag}><strong>${normalizedText}</strong></${tag}>`;
      },
    );

    // CRITICAL: Preserve Roman numerals in Final Debate Summary headers
    // Force uppercase (I., II., III.) to ensure consistency
    html = html.replace(/^(?:\*\*|<strong>)?([IVXLCDM]+)\.\s+(.+?)(?:\*\*|<\/strong>)?$/gim, (match, roman, text) => {
      if (roman.length <= 10) {
        const romanLower = roman.toUpperCase();
        const normalizedText = normalizeHeadingCase(text.trim());
        // Preserve the Roman numeral in the heading and make it the largest section heading
        return `<h1><strong>${romanLower}. ${normalizedText}</strong></h1>`;
      }
      return match;
    });
    // Dynamic heading detection (no longer hardcoded)
    html = html.replace(
      /^(?:#{1,6})\s*(?:SECTION:\s*)?(.+)$/gim,
      (match, text) => {
        const normalizedText = normalizeHeadingCase(text.trim());
        return `<h2><strong>${normalizedText}</strong></h2>`;
      },
    );

    // Detect and convert all-caps headings in HTML (like "ISSUE(S) PRESENTED")
    // First, process all-caps headings that are wrapped in <p> tags
    html = html.replace(
      /<p>\s*([A-Z][A-Z\s()/\-:]+?)\s*<\/p>/g,
      (match, capsText) => {
        const trimmed = capsText.trim();
        // More lenient detection - allow colons and some punctuation
        const isAllCapsHeading =
          trimmed.length >= 3 &&
          trimmed.length < 120 &&
          trimmed === trimmed.toUpperCase() &&
          /[A-Z]/.test(trimmed) &&
          !trimmed.match(/^[-•*▪▫◦‣⁃\d]/) &&
          !trimmed.match(/^\d+[.)]/) &&
          trimmed.split(/\s+/).length <= 10 &&
          !trimmed.match(/^[A-Z\s]+[a-z]/) && // Not mixed case
          (trimmed.endsWith(":") || trimmed.match(/^[A-Z\s()/-]+$/)); // Allow colons or clean all-caps

        if (isAllCapsHeading) {
          // Remove trailing colon if present for normalization
          const textToNormalize = trimmed.endsWith(":")
            ? trimmed.slice(0, -1)
            : trimmed;
          const normalizedText = normalizeHeadingCase(textToNormalize);
          // Render non-numbered all-caps headings as section headings
          return `<h2><strong>${normalizedText}</strong></h2>`;
        }
        return match;
      },
    );

    // Also handle all-caps text that appears between tags or on new lines
    // This handles cases where the text might be between <p> tags and other content
    html = html.replace(
      /(?:<p>|<br\s*\/?>|\n)\s*([A-Z][A-Z\s()/\-:]+?)\s*(?:<\/p>|<br\s*\/?>|\n|$)/g,
      (match, capsText) => {
        const trimmed = capsText.trim();
        // More lenient detection
        const isAllCapsHeading =
          trimmed.length >= 3 &&
          trimmed.length < 120 &&
          trimmed === trimmed.toUpperCase() &&
          /[A-Z]/.test(trimmed) &&
          !trimmed.match(/^[-•*▪▫◦‣⁃\d]/) &&
          !trimmed.match(/^\d+[.)]/) &&
          trimmed.split(/\s+/).length <= 10 &&
          !trimmed.match(/^[A-Z\s]+[a-z]/) && // Not mixed case
          (trimmed.endsWith(":") || trimmed.match(/^[A-Z\s()/-]+$/)) && // Allow colons or clean all-caps
          !match.match(/<[^>]+>/); // Not already inside HTML tags (except the ones we're matching)

        if (isAllCapsHeading) {
          const textToNormalize = trimmed.endsWith(":")
            ? trimmed.slice(0, -1)
            : trimmed;
          const normalizedText = normalizeHeadingCase(textToNormalize);
          // Replace the entire match with the heading, preserving surrounding structure
          return match.replace(
            capsText,
            `<h2><strong>${normalizedText}</strong></h2>`,
          );
        }
        return match;
      },
    );

    // Also handle standalone all-caps text on lines without tags (for plain text that got detected as HTML)
    html = html.replace(
      /^([A-Z][A-Z\s()/\-:]+?)(?:\s*)(?:<br\s*\/?>|\n|$)/gm,
      (match, capsText) => {
        const trimmed = capsText.trim();
        // More lenient detection
        const isAllCapsHeading =
          trimmed.length >= 3 &&
          trimmed.length < 120 &&
          trimmed === trimmed.toUpperCase() &&
          /[A-Z]/.test(trimmed) &&
          !trimmed.match(/^[-•*▪▫◦‣⁃\d]/) &&
          !trimmed.match(/^\d+[.)]/) &&
          trimmed.split(/\s+/).length <= 10 &&
          !trimmed.match(/^[A-Z\s]+[a-z]/) && // Not mixed case
          (trimmed.endsWith(":") || trimmed.match(/^[A-Z\s()/-]+$/)) && // Allow colons or clean all-caps
          !match.includes("<"); // Not already HTML

        if (isAllCapsHeading) {
          const textToNormalize = trimmed.endsWith(":")
            ? trimmed.slice(0, -1)
            : trimmed;
          const normalizedText = normalizeHeadingCase(textToNormalize);
          return `<h2><strong>${normalizedText}</strong></h2>`;
        }
        return match;
      },
    );

    const blockSplitter = /(?=<p>|<br\s*\/?>|\n)/;
    const htmlBlocks = html.split(blockSplitter);
    html = htmlBlocks
      .map((block) => {
        const headingMatch = block.match(
          /^(?:<p>|<br\s*\/?>|\n)?\s*(?:\*\*|<strong>)(?:SECTION:\s*)?(.+?)(?::|(?:\*\*|<\/strong>))\s*(?:<\/p>|(?::|\s|$))/i,
        );
        if (headingMatch && headingMatch.index < 15) {
          const sectionName = headingMatch[1].trim();
          const normalizedText = normalizeHeadingCase(sectionName);
          const remaining = block
            .substring(block.indexOf(headingMatch[0]) + headingMatch[0].length)
            .trim();

          if (
            !remaining ||
            remaining.startsWith("<br") ||
            remaining.startsWith("\n") ||
            remaining.startsWith("</p>")
          ) {
            // <h2> is a block element, so a leading <br> or newline after it
            // renders as an extra blank line — that was the ~35px of dead space
            // under every section heading in the debate round cards. The </p>
            // is unmatched here too (its opening <p> was consumed into the
            // heading match), so strip that as well rather than emit stray tags.
            const tail = remaining.replace(/^(?:\s|<br\s*\/?>|<\/p>)+/gi, "");
            return `<h2><strong>${normalizedText}</strong></h2>${tail}`;
          }
          return `<h2><strong>${normalizedText}</strong></h2><p>${remaining}</p>`;
        }
        return block;
      })
      .join("");
    // Convert any ordered lists to unordered lists for consistent UI rendering.
    html = html.replace(/<ol(\s[^>]*?)?>/gi, (match, attrs) => {
      const safeAttrs = attrs || "";
      return `<ul${safeAttrs}>`;
    });
    html = html.replace(/<\/ol>/gi, "</ul>");

    // Only process list items to clean up formatting
    html = html.replace(
      /<li>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/li>/gi,
      (match, content) => {
        // This pattern matches: "1. ", "1) ", "2. ", "10. ", etc. at the start
        // Strip numbers from list items (we always display unordered lists)
        const numberMatch = content.match(/^(\s*)(\d+)([.)])\s+(.+)$/);
        if (numberMatch) {
          return `<li>${numberMatch[1]}${numberMatch[4]}</li>`;
        }

        const numberMatchWithTags = content.match(
          /^(\s*)(<[^>]+>)?(\s*)(\d+)([.)])\s+(.+?)(<\/[^>]+>)?$/,
        );
        if (numberMatchWithTags && numberMatchWithTags[4]) {
          const prefix = numberMatchWithTags[1] || "";
          const openingTag = numberMatchWithTags[2] || "";
          const closingTag = numberMatchWithTags[7] || "";
          const text = numberMatchWithTags[6] || "";
          return `<li>${prefix}${openingTag}${text}${closingTag}</li>`;
        }

        const bulletMatch = content.match(/^(\s*)([-•*▪▫◦‣⁃])\s+(.+)$/);
        if (bulletMatch) {
          return `<li>${bulletMatch[1]}${bulletMatch[3]}</li>`;
        }

        return match;
      },
    );

    html = html.replace(
      /<h1>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h1>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        text = normalizeHeadingCase(text);
        return `<h1><strong>${text}</strong></h1>`;
      },
    );
    html = html.replace(
      /<h2>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h2>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        text = normalizeHeadingCase(text);
        return `<h2><strong>${text}</strong></h2>`;
      },
    );
    html = html.replace(
      /<h3>([^<]*(?:<[^>]+>[^<]*<\/[^>]+>[^<]*)*)<\/h3>/gi,
      (match, content) => {
        let text = content.replace(/<\/?strong>/g, "").trim();
        text = normalizeHeadingCase(text);
        return `<h3><strong>${text}</strong></h3>`;
      },
    );

    return html;
  }

  // Consumer answers close with a Disclaimer section. Perplexity emits the
  // label as a bare line rather than "### Disclaimer", so it rendered as body
  // copy while every other heading was bold. Promote it to the markdown
  // heading the rest of this function already renders correctly.
  //
  // Applied here rather than to processedValue above: this branch restarts
  // from the original `value`, so anything done to processedValue earlier
  // never reaches it.
  const withDisclaimerHeading = value.replace(
    /^[ \t]*Disclaimer[ \t]*:?[ \t]*$/gim,
    "### Disclaimer",
  );

  let cleanedValue = withDisclaimerHeading.replace(/\*/g, "");

  // CRITICAL FIX: Fix malformed numbered items where markdown bold was removed incorrectly
  // Pattern like "**1. Priority**" becomes "1. Priority" but sometimes becomes "1Priority"
  cleanedValue = cleanedValue.replace(/^(\d+)([A-Z])/gm, "$1. $2");

  const lines = cleanedValue.split("\n");
  const formattedLines = [];
  let inList = false;
  let listItems = [];

  const closeList = () => {
    if (listItems.length > 0) {
      formattedLines.push(`<ul>${listItems.join("")}</ul>`);
      listItems = [];
    }
    inList = false;
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      closeList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(?:SECTION:\s*)?(.+)$/);
    if (headingMatch) {
      closeList();
      // Strip "SECTION:" if it's still in the text
      let headingText = headingMatch[2].trim().replace(/^SECTION:\s*/i, "");
      headingText = normalizeHeadingCase(headingText);
      // Keep numbered headings as true headings if they start with Roman numerals
      if (/^[IVX]+\.\s+/i.test(headingText)) {
        formattedLines.push(`<h1><strong>${headingText}</strong></h1>`);
      } else {
        // Other markdown-style headings render as regular section headings
        formattedLines.push(
          `<h2><strong>${headingText}</strong></h2>`,
        );
      }
      continue;
    }

    const romanMatch = trimmedLine.match(/^([IVXLCDM]+)\.\s+(.+)$/i);
    if (romanMatch && romanMatch[1].length <= 10) {
      // Limit to reasonable Roman numeral length
      closeList();
      // Convert Roman numeral to uppercase to ensure proper formatting (I, II, not Ii)
      const romanNumeral = romanMatch[1].toUpperCase();
      let headingText = romanMatch[2].trim();
      headingText = normalizeHeadingCase(headingText);
      // Preserve the Roman numeral in the heading and render as largest section heading
      formattedLines.push(
        `<h1><strong>${romanNumeral}. ${headingText}</strong></h1>`,
      );
      continue;
    }

    const bulletMatch = trimmedLine.match(/^([-•*▪▫◦‣⁃])\s+(.+)$/);
    const numberMatch = trimmedLine.match(/^(\d+[.)])\s+(.+)$/);

    if (bulletMatch) {
      listItems.push(`<li>${bulletMatch[2]}</li>`);
      inList = true;
      continue;
    } else if (numberMatch) {
      listItems.push(`<li>${numberMatch[2]}</li>`);
      inList = true;
      continue;
    }

    // Dynamic heading detection

    const dynamicHeadingMatch = trimmedLine.match(
      /^(?:(?:#{1,6})\s*(?:SECTION:\s*)?|(?:(?:\*\*|SECTION:)\s*)(?:SECTION:\s*)?)(.+?)(?::|\*\*|$)/i,
    );

    if (dynamicHeadingMatch) {
      closeList();
      const sectionName = dynamicHeadingMatch[1].trim();
      const remainingContent = trimmedLine
        .substring(dynamicHeadingMatch[0].length)
        .trim();
      const normalizedText = normalizeHeadingCase(sectionName);

      // Numbered headings remain true headings; others are regular section headings
      if (/^[IVXLCDM]+\.\s+/i.test(normalizedText)) {
        formattedLines.push(
          `<h1><strong>${normalizedText}</strong></h1>`,
        );
      } else {
        formattedLines.push(
          `<h2><strong>${normalizedText}</strong></h2>`,
        );
      }
      if (remainingContent) {
        formattedLines.push(`<p>${remainingContent}</p>`);
      }
      continue;
    }

    // Detect all-caps headings (common in legal documents like "ISSUE(S) PRESENTED")
    // Pattern: All uppercase, reasonable length, followed by blank line or content
    const isAllCapsHeading =
      trimmedLine.length >= 3 &&
      trimmedLine.length < 120 &&
      trimmedLine === trimmedLine.toUpperCase() &&
      /[A-Z]/.test(trimmedLine) &&
      !trimmedLine.match(/^[-•*▪▫◦‣⁃\d]/) && // Not a bullet or numbered list
      !trimmedLine.match(/^\d+[.)]/) && // Not a numbered item
      !trimmedLine.match(/^#{1,6}\s/) && // Not already a markdown heading
      trimmedLine.split(/\s+/).length <= 10; // Reasonable word count for a heading

    if (isAllCapsHeading) {
      const nextLine = index < lines.length - 1 ? lines[index + 1].trim() : "";
      const prevLine = index > 0 ? lines[index - 1].trim() : "";

      // If preceded by blank line and followed by content or blank, it's likely a heading
      if (prevLine === "" || (nextLine !== "" && !/^[-•\d]/.test(nextLine))) {
        closeList();
        let headingText = normalizeHeadingCase(trimmedLine);
        // Non-numbered all-caps headings render as regular section headings
        formattedLines.push(
          `<h2><strong>${headingText}</strong></h2>`,
        );
        continue;
      }
    }

    if (
      trimmedLine.length > 0 &&
      trimmedLine.length < 120 &&
      trimmedLine.endsWith(":")
    ) {
      closeList();

      let headingText = trimmedLine.slice(0, -1);
      headingText = normalizeHeadingCase(headingText);
      // Colon-terminated headings render as regular section headings
      formattedLines.push(
        `<h2><strong>${headingText}</strong></h2>`,
      );
      continue;
    }

    closeList();
    formattedLines.push(
      `<p>${line}</p>`,
    );
  }

  closeList();

  return formattedLines.join("");
};